<script setup lang="ts">
import {computed, ref} from 'vue';
import {PulseTimeAllocation} from '@/types/DataTypes.ts';
import {useTimeAllocationCategoriesStore} from '@/stores/timeAllocationCategories.store.ts';
import {useTimeAllocation} from '@/composables/useTimeAllocation.ts';
import {useCategoryVisibility} from '@/composables/useCategoryVisibility.ts';
import {useAllocationBar} from '@/composables/useAllocationBar.ts';

import CategoryChips from './CategoryChips.vue';
import AllocationBar from './AllocationBar.vue';
import CategoryDetailsList from './CategoryDetailsList.vue';

const props = defineProps<{
  modelValue: PulseTimeAllocation | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: PulseTimeAllocation | null];
}>();

const categoriesStore = useTimeAllocationCategoriesStore();

// Use reactive categories from store
const categories = computed(() => categoriesStore.active);

// State for showing unused categories
const showUnusedCategories = ref(false);

// Use composables for business logic
const {
  localAllocation,
  enabledCategories,
  total,
  isValid,
  enableCategory,
  disableCategory,
  adjustCategoryPercentage,
  canDecrease,
  canIncrease
} = useTimeAllocation(props.modelValue, emit);

const { shouldShowMoreLink, getCategoriesToShow } = useCategoryVisibility();

const { barSegments } = useAllocationBar(localAllocation, enabledCategories);

// Get categories to show in chips
const disabledCategoriesList = computed(() => {
  const categoriesToShow = getCategoriesToShow(showUnusedCategories.value);
  return categoriesToShow.filter(category => 
    (localAllocation.value[category.key as keyof PulseTimeAllocation] || 0) === 0
  );
});

function toggleUnusedCategories() {
  showUnusedCategories.value = !showUnusedCategories.value;
}

// Handle drag updates from AllocationBar
function handleAllocationUpdate(key: string, value: number) {
  localAllocation.value[key as keyof PulseTimeAllocation] = value;
}

</script>

<template>
  <div class="pulse-time-allocation-panel">
    <!-- Loading state -->
    <div v-if="categoriesStore.loading" class="flex items-center justify-center py-8">
      <i class="pi pi-spinner pi-spin text-2xl text-primary"></i>
      <span class="ml-2 text-sm text-muted-color">Loading categories...</span>
    </div>

    <!-- Error state -->
    <div v-else-if="categoriesStore.error" class="flex items-center justify-center py-8 text-red-600">
      <i class="pi pi-exclamation-triangle text-xl"></i>
      <span class="ml-2 text-sm">{{ categoriesStore.error }}</span>
    </div>

    <!-- Categories content -->
    <template v-else-if="categories.length > 0">
      <!-- Disabled categories as chips -->
      <CategoryChips
        :categories="disabledCategoriesList"
        :should-show-more-link="shouldShowMoreLink"
        :show-unused-categories="showUnusedCategories"
        :disabled="disabled"
        @enable-category="enableCategory"
        @toggle-unused-categories="toggleUnusedCategories"
      />

      <!-- Single Bar Interface -->
      <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 transform -translate-y-2"
          enter-to-class="opacity-100 transform translate-y-0"
          leave-active-class="transition-all duration-300 ease-in"
          leave-from-class="opacity-100 transform translate-y-0"
          leave-to-class="opacity-0 transform -translate-y-2"
      >
        <div v-if="enabledCategories.length > 0" class="space-y-4">
          <!-- Bar visualization -->
          <div class="space-y-2">
            <AllocationBar
              :segments="barSegments"
              :local-allocation="localAllocation"
              :enabled-categories="enabledCategories"
              :disabled="disabled"
              @update-allocation="handleAllocationUpdate"
            />
          </div>

          <!-- Category details -->
          <CategoryDetailsList
            :segments="barSegments"
            :local-allocation="localAllocation"
            :disabled="disabled"
            :can-decrease="canDecrease"
            :can-increase="canIncrease"
            @adjust-percentage="adjustCategoryPercentage"
            @disable-category="disableCategory"
          />

          <div v-if="!isValid && total > 0" class="text-xs text-red-600">
            Must sum to 100% with at least one non-zero value. Current total: {{ total }}%
            {{localAllocation}}
          </div>
        </div>
      </Transition>
    </template>
  </div>
</template>
